/**
 * Assistente de IA (piloto): conversas por pessoa e por projeto, resposta em tempo real, processo/caso anexado,
 * custo e limite do mês sempre à vista. Administrador: aba Projetos (instruções, modelo, membros, arquivos, limite).
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useSearch, useTitle } from "@/lib/router";
import { fmtDateTime } from "@/lib/format";
import { useCatalog } from "@/lib/catalog";
import { confirmar, toast } from "@/ui/feedback";
import { Badge, Card, EntityPicker, ErrorBox, Field, FormGrid, Loading, Modal, PageHeader, Tabs, useAct, type EntityRef } from "@/ui/kit";
import { Markdown } from "@/ui/md";

interface Limit { spentBrl: number; limitBrl: number | null; level: "ok" | "aviso" | "bloqueado"; pct: number | null }
interface State {
  canUse: boolean; admin: boolean; enabled: boolean; keyConfigured: boolean; priced: boolean; missingPrice: string[]; usdBrl: number | null;
  limit: Limit | null; projects: Array<{ id: string; name: string; description: string | null; model: string; active: boolean; member: boolean }>; models: string[];
}
interface ConvRow { id: string; title: string; projectId: string | null; projectName: string | null; updatedAt: string; author: string; mine: boolean; processId: string | null; caseId: string | null }
interface Msg { id: string; role: "user" | "assistant"; content: string; author: string | null; createdAt: string; error: string | null; costBrl: number | null; pending?: boolean }
interface Conv {
  id: string; title: string; mine: boolean; author: string; archived: boolean;
  project: { id: string; name: string; model: string } | null;
  process: { id: string; label: string } | null; case: { id: string; label: string } | null;
  messages: Msg[]; limits: { user: Limit; project: Limit | null };
}

export const brl = (n: number | null | undefined) => (n === null || n === undefined ? "—" : `R$ ${n.toFixed(2).replace(".", ",")}`);

function LimitBar({ label, l }: { label: string; l: Limit }): JSX.Element {
  const pct = l.pct ?? 0;
  const tone = l.level === "bloqueado" ? "bad" : l.level === "aviso" ? "warn" : "ok";
  return (
    <div className={`asst-limit ${tone}`} title={l.limitBrl === null ? "Sem limite" : `${pct}% do limite`}>
      <span>{label}: <strong>{brl(l.spentBrl)}</strong>{l.limitBrl !== null ? <> de {brl(l.limitBrl)}</> : " (sem limite)"}</span>
      {l.limitBrl !== null && <span className="asst-limit-bar"><span style={{ width: `${Math.min(100, pct)}%` }} /></span>}
    </div>
  );
}

export function AssistantPage(): JSX.Element {
  useTitle("Assistente");
  const [search, set] = useSearch();
  const tab = search.get("aba") === "projetos" ? "projetos" : "conversas";
  const { data: st, error } = useQuery({ queryKey: ["assistente-estado"], queryFn: () => api.get<State>("/assistente/estado") });
  if (!st) return <div className="stack gap-16"><PageHeader title="Assistente" />{error ? <ErrorBox error={error} /> : <Loading />}</div>;
  return (
    <div className="stack gap-16">
      <PageHeader title={<>Assistente <Badge tone="info">piloto</Badge></>} subtitle="IA para pesquisar, redigir e revisar — com custo medido e limite mensal" />
      {st.admin && <Tabs tabs={[{ key: "conversas", label: "Conversas" }, { key: "projetos", label: "Projetos" }]} value={tab} onChange={(k) => set({ aba: k === "conversas" ? null : k })} />}
      {!st.keyConfigured || !st.enabled ? <div className="warn-box">A IA está desligada. O administrador liga em <strong>Configurações › Automação e IA › IA</strong> (a chave fica no Secrets Manager).</div>
        : !st.priced ? <div className="warn-box">Falta informar o preço do modelo e a cotação do dólar em <strong>Configurações › Automação e IA › IA</strong>. Sem isso o limite de gasto não funciona e o Assistente fica bloqueado.</div> : null}
      {tab === "projetos" && st.admin ? <ProjectsAdmin models={st.models} /> : !st.canUse
        ? <Card><div className="muted">O Assistente está em piloto. Peça ao administrador a permissão <strong>Usar o Assistente de IA</strong>.</div></Card>
        : <Conversations st={st} />}
    </div>
  );
}

// ───────────────────────────── conversas

function Conversations({ st }: { st: State }): JSX.Element {
  const qc = useQueryClient();
  const [search, set] = useSearch();
  const current = search.get("c");
  const [archived, setArchived] = useState(false);
  const [newProject, setNewProject] = useState<string>("");
  const { data: list } = useQuery({ queryKey: ["assistente-conversas", archived], queryFn: () => api.get<ConvRow[]>(`/assistente/conversas${archived ? "?arquivadas=1" : ""}`) });
  const create = useAct(() => api.post<{ id: string }>("/assistente/conversas", { projectId: newProject || null }), [["assistente-conversas"]], (r) => set({ c: r.id }), false);
  const myProjects = st.projects.filter((p) => p.member && p.active);
  const groups = new Map<string, ConvRow[]>();
  for (const c of list ?? []) { const k = c.projectName ?? "Minhas conversas"; groups.set(k, [...(groups.get(k) ?? []), c]); }

  return (
    <div className="chat-layout asst-layout">
      <Card className="chat-side">
        <div className="stack gap-8">
          {st.limit && <LimitBar label="Seu uso no mês" l={st.limit} />}
          <div className="row gap-4 nowrap">
            <select className="input" value={newProject} onChange={(e) => setNewProject(e.target.value)} title="Projeto da nova conversa">
              <option value="">Sem projeto (só você vê)</option>
              {myProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button className="btn primary" disabled={create.isPending} onClick={() => create.mutate()}>Nova</button>
          </div>
          <ErrorBox error={create.error} />
          <label className="check small"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Ver arquivadas</label>
          {!list ? <Loading /> : list.length === 0 ? <div className="muted small">Nenhuma conversa ainda.</div> : [...groups.entries()].map(([g, rows]) => (
            <div key={g} className="stack gap-4">
              <div className="nav-section asst-group">{g}</div>
              {rows.map((c) => (
                <button key={c.id} className={`chat-conv ${c.id === current ? "active" : ""}`} onClick={() => { set({ c: c.id }); void qc.invalidateQueries({ queryKey: ["assistente-conversa", c.id] }); }}>
                  <div className="stack gap-0" style={{ minWidth: 0 }}>
                    <span className="ellipsis">{c.title}</span>
                    <span className="muted small">{c.mine ? "" : `${c.author} · `}{fmtDateTime(c.updatedAt)}{c.processId || c.caseId ? " · 📎" : ""}</span>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>
      </Card>
      <Card className="chat-main">
        {current ? <ConversationView key={current} id={current} /> : (
          <div className="asst-empty">
            <div className="stack gap-8">
              <strong>Como usar</strong>
              <div className="muted small">Comece uma conversa nova à esquerda. Escolha um projeto para usar as instruções e os arquivos dele (e para a equipe do projeto ver a conversa).</div>
              <div className="muted small">Anexe um processo ou caso para a IA considerar os dados dele. Tudo que a IA responde é sugestão: confira leis, julgados, datas e valores antes de usar.</div>
              <div className="muted small">O que você escreve é enviado ao provedor de IA (Anthropic). Não cole senhas nem dados que não precisam ir.</div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function ConversationView({ id }: { id: string }): JSX.Element {
  const qc = useQueryClient();
  const [, set] = useSearch();
  const { data: c, error } = useQuery({ queryKey: ["assistente-conversa", id], queryFn: () => api.get<Conv>(`/assistente/conversas/${id}`) });
  const [text, setText] = useState("");
  const [live, setLive] = useState<Msg[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [editTitle, setEditTitle] = useState<string | null>(null);
  const [attach, setAttach] = useState(false);
  const stop = useRef<AbortController | null>(null);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [c?.messages.length, live]);
  useEffect(() => () => stop.current?.abort(), []);

  const patch = useAct((b: Record<string, unknown>) => api.patch(`/assistente/conversas/${id}`, b), [["assistente-conversa", id], ["assistente-conversas"]], () => { setEditTitle(null); setAttach(false); }, false);
  const del = useAct(() => api.del(`/assistente/conversas/${id}`), [["assistente-conversas"]], () => set({ c: null }), "Conversa excluída");

  async function send(): Promise<void> {
    const q = text.trim();
    if (!q || busy) return;
    setBusy(true); setErr(null); setText("");
    const tmpUser: Msg = { id: "u", role: "user", content: q, author: null, createdAt: new Date().toISOString(), error: null, costBrl: null };
    const tmpAsst: Msg = { id: "a", role: "assistant", content: "", author: null, createdAt: new Date().toISOString(), error: null, costBrl: null, pending: true };
    setLive([tmpUser, tmpAsst]);
    const ac = new AbortController(); stop.current = ac;
    try {
      const res = await api.stream(`/assistente/conversas/${id}/mensagens`, { text: q });
      const reader = res.body!.getReader();
      const dec = new TextDecoder();
      let buf = ""; let acc = "";
      ac.signal.addEventListener("abort", () => { void reader.cancel(); });
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let i: number;
        while ((i = buf.indexOf("\n\n")) >= 0) {
          const chunk = buf.slice(0, i); buf = buf.slice(i + 2);
          const line = chunk.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          const ev = JSON.parse(line.slice(6)) as { t: string; x?: string; message?: string; costBrl?: number | null };
          if (ev.t === "delta" && ev.x) { acc += ev.x; setLive([tmpUser, { ...tmpAsst, content: acc }]); }
          else if (ev.t === "error") setErr(new Error(ev.message ?? "Falha na resposta."));
        }
      }
    } catch (e) {
      if (!ac.signal.aborted) { setErr(e); setText(q); }
    } finally {
      stop.current = null;
      await qc.invalidateQueries({ queryKey: ["assistente-conversa", id] });
      setBusy(false); setLive([]);
      void qc.invalidateQueries({ queryKey: ["assistente-conversas"] });
      void qc.invalidateQueries({ queryKey: ["assistente-estado"] });
    }
  }

  if (!c) return <div className="pad">{error ? <ErrorBox error={error} /> : <Loading />}</div>;
  const msgs = [...c.messages, ...live];
  const blocked = c.limits.user.level === "bloqueado" || c.limits.project?.level === "bloqueado";
  const total = c.messages.reduce((a, m) => a + (m.costBrl ?? 0), 0);
  return (
    <div className="asst-conv">
      <div className="asst-head">
        <div className="stack gap-4" style={{ minWidth: 0, flex: 1 }}>
          {editTitle !== null ? (
            <div className="row gap-4 nowrap">
              <input className="input" autoFocus value={editTitle} onChange={(e) => setEditTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && editTitle.trim()) patch.mutate({ title: editTitle.trim() }); if (e.key === "Escape") setEditTitle(null); }} />
              <button className="btn small" onClick={() => editTitle.trim() && patch.mutate({ title: editTitle.trim() })}>Salvar</button>
            </div>
          ) : <strong className="ellipsis" onDoubleClick={() => c.mine && setEditTitle(c.title)} title={c.mine ? "Clique duas vezes para renomear" : undefined}>{c.title}</strong>}
          <div className="row gap-4 small muted">
            {c.project ? <Badge tone="info">{c.project.name}</Badge> : <Badge tone="muted">só você vê</Badge>}
            {!c.mine && <span>de {c.author}</span>}
            {c.process && <Badge>📎 {c.process.label}</Badge>}
            {c.case && <Badge>📎 {c.case.label}</Badge>}
            <span title="Custo das respostas desta conversa">· {brl(total)} nesta conversa</span>
          </div>
        </div>
        {c.mine && (
          <div className="row gap-4 nowrap">
            <button className="btn small" onClick={() => setAttach(true)}>{c.process || c.case ? "Trocar anexo" : "Anexar processo/caso"}</button>
            <button className="btn small" onClick={() => patch.mutate({ archived: !c.archived })}>{c.archived ? "Desarquivar" : "Arquivar"}</button>
            <button className="btn small danger" onClick={() => void confirmar("Excluir esta conversa de vez? As mensagens somem (o registro de custo fica).", { danger: true, confirm: "Excluir" }).then((ok) => ok && del.mutate())}>Excluir</button>
          </div>
        )}
      </div>
      <div className="asst-limits">
        <LimitBar label="Seu uso no mês" l={c.limits.user} />
        {c.limits.project && <LimitBar label="Projeto no mês" l={c.limits.project} />}
      </div>
      <div className="asst-msgs">
        {msgs.length === 0 && <div className="muted small pad">Escreva sua pergunta abaixo. Ex.: "Resuma os pontos principais deste processo e o que falta fazer".</div>}
        {msgs.map((m, i) => (
          <div key={`${m.id}-${i}`} className={`asst-msg ${m.role}`}>
            {m.role === "user" ? (
              <div className="asst-bubble">{m.author && !c.mine ? <div className="small muted">{m.author}</div> : null}<div className="pre-wrap">{m.content}</div></div>
            ) : (
              <div className="asst-answer">
                {m.content ? <Markdown text={m.content} /> : m.pending ? <span className="muted">Pensando…</span> : null}
                {m.error && <div className="text-bad small">⚠ {m.error}</div>}
                {!m.pending && m.content && (
                  <div className="row gap-8 small muted asst-meta">
                    <button className="link-btn" onClick={() => void navigator.clipboard.writeText(m.content).then(() => toast.ok("Copiado"))}>Copiar</button>
                    {m.costBrl !== null && <span>{brl(m.costBrl)}</span>}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        <div ref={end} />
      </div>
      <ErrorBox error={err} />
      <div className="asst-input">
        <textarea className="input" rows={3} value={text} disabled={busy || blocked || c.archived}
          placeholder={c.archived ? "Conversa arquivada." : blocked ? "Limite do mês atingido." : "Escreva… (Enter envia, Shift+Enter quebra linha)"}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }} />
        {busy ? <button className="btn" onClick={() => stop.current?.abort()}>Parar</button>
          : <button className="btn primary" disabled={!text.trim() || blocked || c.archived} onClick={() => void send()}>Enviar</button>}
      </div>
      {attach && <AttachModal c={c} onClose={() => setAttach(false)} onSave={(v) => patch.mutate(v)} pending={patch.isPending} error={patch.error} />}
    </div>
  );
}

function AttachModal({ c, onClose, onSave, pending, error }: { c: Conv; onClose: () => void; onSave: (v: { processId: string | null; caseId: string | null }) => void; pending: boolean; error: unknown }): JSX.Element {
  const [v, setV] = useState<EntityRef | null>(c.process ? { kind: "processo", id: c.process.id, label: c.process.label } : c.case ? { kind: "caso", id: c.case.id, label: c.case.label } : null);
  return (
    <Modal title="Processo ou caso da conversa" onClose={onClose} footer={<>
      <button className="btn" onClick={onClose}>Cancelar</button>
      <button className="btn primary" disabled={pending} onClick={() => onSave({ processId: v?.kind === "processo" ? v.id : null, caseId: v?.kind === "caso" ? v.id : null })}>Salvar</button>
    </>}>
      <div className="stack gap-8">
        <EntityPicker kinds={["processo", "caso"]} value={v} onChange={setV} placeholder="Buscar processo ou caso…" />
        <div className="muted small">
          Os dados (partes, fase, situação e as últimas movimentações) vão para a IA a cada pergunta. Financeiro e anotações internas não vão.
          {c.project && " Na conversa de projeto, só os membros que podem ver esse processo/caso enxergam a conversa."}
        </div>
        <ErrorBox error={error} />
      </div>
    </Modal>
  );
}

// ───────────────────────────── projetos (administrador)

interface Project {
  id: string; name: string; description: string | null; instructions: string; model: string; monthlyLimitBrl: number | null; active: boolean;
  memberIds: string[]; files: Array<{ id: string; name: string; size: number; chars: number; createdAt: string }>; totalChars: number; maxChars: number; spentBrl: number;
}

function ProjectsAdmin({ models }: { models: string[] }): JSX.Element {
  const { data } = useQuery({ queryKey: ["assistente-projetos"], queryFn: () => api.get<Project[]>("/assistente/projetos") });
  const [edit, setEdit] = useState<Project | "novo" | null>(null);
  const { data: cat } = useCatalog();
  const name = new Map((cat?.users ?? []).map((u) => [u.id, u.displayName]));
  return (
    <Card title="Projetos" actions={<button className="btn primary" onClick={() => setEdit("novo")}>Novo projeto</button>}>
      <div className="stack gap-8">
        <div className="muted small">
          Projeto = instruções fixas + arquivos de referência + modelo + quem participa. Os membros veem as conversas do projeto entre si.
          Lembre de conceder <strong>Usar o Assistente de IA</strong> (Configurações › Usuários) a quem vai participar do piloto.
        </div>
        {!data ? <Loading /> : data.length === 0 ? <div className="muted">Nenhum projeto ainda.</div> : (
          <table className="table">
            <thead><tr><th>Projeto</th><th>Modelo</th><th>Membros</th><th>Arquivos</th><th className="right">Gasto no mês</th><th /></tr></thead>
            <tbody>{data.map((p) => (
              <tr key={p.id} className={p.active ? "" : "muted"}>
                <td><strong>{p.name}</strong>{!p.active && <> <Badge tone="muted">desativado</Badge></>}{p.description && <div className="small muted">{p.description}</div>}</td>
                <td className="small">{p.model}</td>
                <td className="small">{p.memberIds.map((id) => name.get(id) ?? "?").join(", ") || "—"}</td>
                <td className="small">{p.files.length} ({Math.round(p.totalChars / 1000)} mil caracteres)</td>
                <td className="right">{brl(p.spentBrl)}{p.monthlyLimitBrl !== null && <span className="muted small"> / {brl(p.monthlyLimitBrl)}</span>}</td>
                <td className="right"><button className="btn small" onClick={() => setEdit(p)}>Editar</button></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
      {edit && <ProjectModal p={edit === "novo" ? null : edit} models={models} onClose={() => setEdit(null)} />}
    </Card>
  );
}

function ProjectModal({ p, models, onClose }: { p: Project | null; models: string[]; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const { data: cat } = useCatalog();
  const [f, setF] = useState({
    name: p?.name ?? "", description: p?.description ?? "", instructions: p?.instructions ?? "", model: p?.model ?? models[0] ?? "",
    limit: p?.monthlyLimitBrl?.toString() ?? "", active: p?.active ?? true, memberIds: new Set(p?.memberIds ?? []),
  });
  const body = () => ({
    name: f.name.trim(), description: f.description.trim() || null, instructions: f.instructions, model: f.model,
    monthlyLimitBrl: f.limit.trim() === "" ? null : Number(f.limit.replace(",", ".")), active: f.active, memberIds: [...f.memberIds],
  });
  const save = useAct(() => (p ? api.patch(`/assistente/projetos/${p.id}`, body()) : api.post("/assistente/projetos", body())), [["assistente-projetos"], ["assistente-estado"]], onClose, "Projeto salvo");
  const remove = useAct(() => api.del(`/assistente/projetos/${p!.id}`), [["assistente-projetos"], ["assistente-estado"]], onClose, "Projeto excluído");
  const delFile = useAct((fid: string) => api.del(`/assistente/projetos/${p!.id}/arquivos/${fid}`), [["assistente-projetos"]], undefined, "Arquivo removido");
  const [up, setUp] = useState<{ busy: boolean; err: unknown }>({ busy: false, err: null });
  const current = p ? (qc.getQueryData<Project[]>(["assistente-projetos"]) ?? []).find((x) => x.id === p.id) ?? p : null;

  async function upload(file: File): Promise<void> {
    if (!p) return;
    setUp({ busy: true, err: null });
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.upload(`/assistente/projetos/${p.id}/arquivos`, fd);
      await qc.invalidateQueries({ queryKey: ["assistente-projetos"] });
      toast.ok("Arquivo adicionado");
      setUp({ busy: false, err: null });
    } catch (e) { setUp({ busy: false, err: e }); }
  }

  return (
    <Modal wide title={p ? `Projeto: ${p.name}` : "Novo projeto"} onClose={onClose} footer={<>
      {p && <button className="btn danger" style={{ marginRight: "auto" }} onClick={() => void confirmar("Excluir o projeto? Só é possível se ele não tiver conversas (senão, desative).", { danger: true, confirm: "Excluir" }).then((ok) => ok && remove.mutate())}>Excluir</button>}
      <button className="btn" onClick={onClose}>Cancelar</button>
      <button className="btn primary" disabled={save.isPending || f.name.trim().length < 2 || !f.model} onClick={() => save.mutate()}>Salvar</button>
    </>}>
      <div className="stack gap-12">
        <FormGrid>
          <Field label="Nome" required><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex.: Peças trabalhistas" /></Field>
          <Field label="Modelo" required hint="Modelos liberados no servidor (AI_MODEL / AI_MODELS). Cada um tem preço próprio.">
            <select className="input" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })}>{models.map((m) => <option key={m} value={m}>{m}</option>)}</select>
          </Field>
          <Field label="Limite do projeto no mês (R$)" hint="Vazio = sem limite próprio (vale o limite de cada pessoa).">
            <input className="input" inputMode="decimal" value={f.limit} onChange={(e) => setF({ ...f, limit: e.target.value })} />
          </Field>
          <Field label="Descrição" span={3}><input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <Field label="Instruções (vão em toda pergunta do projeto)" span={3}>
            <textarea className="input" rows={7} value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })}
              placeholder={"Ex.: Você auxilia na redação de peças trabalhistas do escritório.\nTom formal, frases curtas. Cite CLT e súmulas do TST quando pertinente, sempre avisando para conferir.\nEstrutura padrão: fatos, fundamentos, pedidos."} />
          </Field>
        </FormGrid>
        <label className="check"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Projeto ativo</label>
        <div className="stack gap-4">
          <strong className="small">Membros (veem as conversas do projeto entre si)</strong>
          <div className="asst-members">
            {(cat?.users ?? []).map((u) => (
              <label key={u.id} className="check small">
                <input type="checkbox" checked={f.memberIds.has(u.id)} onChange={(e) => { const s = new Set(f.memberIds); if (e.target.checked) s.add(u.id); else s.delete(u.id); setF({ ...f, memberIds: s }); }} /> {u.displayName}
              </label>
            ))}
          </div>
        </div>
        <div className="stack gap-4">
          <strong className="small">Arquivos de referência</strong>
          {!p ? <div className="muted small">Salve o projeto para enviar arquivos.</div> : (
            <>
              <div className="muted small">
                PDF com texto, Word (.docx) ou texto. Guardamos só o texto. Tudo vai para a IA em toda pergunta (com cache, que barateia a repetição):
                use modelos e trechos que realmente ajudam. Usado: {Math.round((current?.totalChars ?? 0) / 1000)} de {Math.round((current?.maxChars ?? 400000) / 1000)} mil caracteres.
              </div>
              {(current?.files ?? []).map((x) => (
                <div key={x.id} className="row gap-8 small">
                  <span>📄 {x.name}</span><span className="muted">{Math.round(x.chars / 1000)} mil caracteres</span>
                  <button className="link-btn" onClick={() => void confirmar(`Remover ${x.name}?`, { danger: true, confirm: "Remover" }).then((ok) => ok && delFile.mutate(x.id))}>remover</button>
                </div>
              ))}
              <label className="btn small" style={{ alignSelf: "flex-start" }}>
                {up.busy ? "Lendo arquivo…" : "Adicionar arquivo"}
                <input type="file" hidden accept=".pdf,.docx,.txt,.md,.csv" disabled={up.busy} onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; if (file) void upload(file); }} />
              </label>
              <ErrorBox error={up.err} />
            </>
          )}
        </div>
        <ErrorBox error={save.error ?? remove.error ?? delFile.error} />
      </div>
    </Modal>
  );
}
